import { useState, useRef, useEffect, useId } from 'react';
import { CaretDown, Check } from '@phosphor-icons/react';

export default function Select({ 
  options, 
  value, 
  onChange, 
  placeholder = 'Select...',
  className = '',
  disabled = false,
  error = null,
  id: providedId,
  'aria-label': ariaLabel,
  title,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef(null);
  const buttonRef = useRef(null);
  const listRef = useRef(null);
  const generatedId = useId();
  const selectId = providedId || generatedId;
  const listboxId = `${selectId}-listbox`;

  // Find the selected option label
  const selectedOption = options.find(opt => opt.value === value);
  const displayLabel = selectedOption?.label || placeholder;
  const selectedIndex = options.findIndex(opt => opt.value === value);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Keyboard navigation
  useEffect(() => {
    function handleKeyDown(event) {
      if (!isOpen) return;
      
      switch (event.key) {
        case 'Escape':
          event.preventDefault();
          setIsOpen(false);
          buttonRef.current?.focus();
          break;
        case 'ArrowDown':
          event.preventDefault();
          setActiveIndex(prev => Math.min(prev + 1, options.length - 1));
          break;
        case 'ArrowUp':
          event.preventDefault();
          setActiveIndex(prev => Math.max(prev - 1, 0));
          break;
        case 'Enter':
        case ' ':
          event.preventDefault();
          if (activeIndex >= 0) {
            handleSelect(options[activeIndex].value);
          }
          break;
        case 'Home':
          event.preventDefault();
          setActiveIndex(0);
          break;
        case 'End':
          event.preventDefault();
          setActiveIndex(options.length - 1);
          break;
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, activeIndex, options]);

  // Reset active index when opening
  useEffect(() => {
    if (isOpen) {
      setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
    }
  }, [isOpen, selectedIndex]);

  function handleSelect(optionValue) {
    onChange(optionValue);
    setIsOpen(false);
    buttonRef.current?.focus();
  }

  function handleToggle() {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  }

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Trigger button */}
      <button
        ref={buttonRef}
        id={selectId}
        type="button"
        onClick={handleToggle}
        disabled={disabled}
        title={title}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-sm text-sm text-left transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
        style={{
          backgroundColor: disabled ? 'var(--color-bg-surface)' : 'var(--color-bg-canvas)',
          border: `1px solid ${error ? 'var(--color-status-error)' : 'var(--color-border-subtle)'}`,
          color: 'var(--color-text-primary)',
        }}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={listboxId}
        aria-label={ariaLabel}
        aria-invalid={error ? 'true' : undefined}
      >
        <span className={!selectedOption ? 'text-muted' : ''}>
          {displayLabel}
        </span>
        <CaretDown 
          size={16} 
          weight="bold"
          className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          style={{ color: 'var(--color-text-muted)' }}
          aria-hidden="true"
        />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <ul 
          ref={listRef}
          id={listboxId}
          role="listbox"
          aria-activedescendant={activeIndex >= 0 ? `${selectId}-option-${activeIndex}` : undefined}
          className="absolute z-50 w-full mt-1 py-1 rounded-sm shadow-lg max-h-60 overflow-auto"
          style={{
            backgroundColor: 'var(--color-bg-canvas)',
            border: '1px solid var(--color-border-subtle)',
          }}
          tabIndex={-1}
        >
          {options.map((option, index) => {
            const isSelected = option.value === value;
            const isActive = activeIndex === index;
            return (
              <li
                key={option.value}
                id={`${selectId}-option-${index}`}
                role="option"
                aria-selected={isSelected}
                onClick={() => handleSelect(option.value)}
                onMouseEnter={() => setActiveIndex(index)}
                className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm text-left transition-colors duration-150 cursor-pointer"
                style={{
                  backgroundColor: isActive 
                    ? 'var(--color-bg-raised)' 
                    : isSelected 
                      ? 'var(--color-bg-surface)' 
                      : 'transparent',
                  color: 'var(--color-text-primary)',
                }}
              >
                <span>{option.label}</span>
                {isSelected && (
                  <Check size={16} weight="bold" style={{ color: 'var(--color-status-success)' }} aria-hidden="true" />
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
